import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import CodeIcon from '@mui/icons-material/Code';
import CodeOffIcon from '@mui/icons-material/CodeOff';
import TitleIcon from '@mui/icons-material/Title';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ImageIcon from '@mui/icons-material/Image';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import useGet from 'hooks/useGet';
import useCreate from 'hooks/useCreate';
import useUpdate from 'hooks/useUpdate';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';

const emptySeo = { metaTitle: '', metaDescription: '', canonicalUrl: '' };

const normalizeBlog = (blog) => ({
  title: blog?.title || '',
  slug: blog?.slug || '',
  excerpt: blog?.excerpt || '',
  content: blog?.content || '',
  tagsText: Array.isArray(blog?.tags) ? blog.tags.join(', ') : '',
  isPublished: Boolean(blog?.isPublished),
  seo: {
    metaTitle: blog?.seo?.metaTitle || '',
    metaDescription: blog?.seo?.metaDescription || '',
    canonicalUrl: blog?.seo?.canonicalUrl || ''
  },
  coverImage: blog?.coverImage || ''
});

const buildFormData = (payload, coverFile) => {
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (key === 'seo') {
      formData.append('seo', JSON.stringify(value));
      return;
    }
    if (key === 'tags') {
      formData.append('tags', JSON.stringify(value));
      return;
    }
    formData.append(key, value);
  });
  if (coverFile) formData.append('coverImage', coverFile);
  return formData;
};

const BlogForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const { data, loading } = useGet(isEdit ? `/api/blogs/${id}` : null);
  const { create } = useCreate('/api/blogs');
  const { update } = useUpdate('/api/blogs');

  const [formState, setFormState] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    tagsText: '',
    isPublished: false,
    seo: emptySeo,
    coverImage: ''
  });
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [hasHydratedEditor, setHasHydratedEditor] = useState(false);

  const blog = useMemo(() => data?.blog || null, [data]);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false, autolink: true, defaultProtocol: 'https' }),
      Image.configure({ inline: false, allowBase64: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] })
    ],
    content: formState.content || '',
    onUpdate: ({ editor: instance }) => {
      setFormState((prev) => ({ ...prev, content: instance.getHTML() }));
    }
  });

  useEffect(() => {
    if (!blog || !isEdit) return;
    const normalized = normalizeBlog(blog);
    setFormState((prev) => ({ ...prev, ...normalized }));
    setCoverPreview(normalized.coverImage || '');
  }, [blog, isEdit]);

  useEffect(() => {
    if (!editor || !isEdit || !blog || hasHydratedEditor) return;
    editor.commands.setContent(normalizeBlog(blog).content || '');
    setHasHydratedEditor(true);
  }, [editor, isEdit, blog, hasHydratedEditor]);

  useEffect(() => {
    return () => {
      if (coverPreview && coverPreview.startsWith('blob:')) {
        URL.revokeObjectURL(coverPreview);
      }
    };
  }, [coverPreview]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSeoChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({
      ...prev,
      seo: {
        ...prev.seo,
        [name]: value
      }
    }));
  };

  const handleCoverChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (coverPreview && coverPreview.startsWith('blob:')) {
      URL.revokeObjectURL(coverPreview);
    }
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    if (!formState.title.trim()) {
      setErrorMessage('Title is required.');
      return;
    }
    const textContent = editor ? editor.getText().trim() : formState.content.trim();
    if (!textContent) {
      setErrorMessage('Content is required.');
      return;
    }

    setSubmitting(true);
    setErrorMessage('');
    try {
      const tags = formState.tagsText
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);

      const payload = {
        title: formState.title.trim(),
        excerpt: formState.excerpt.trim(),
        content: formState.content.trim(),
        isPublished: formState.isPublished,
        seo: formState.seo || emptySeo
      };

      if (tags.length > 0) payload.tags = tags;
      if (formState.slug.trim()) payload.slug = formState.slug.trim();

      const requestPayload = coverFile ? buildFormData(payload, coverFile) : payload;

      if (isEdit) {
        await update(id, requestPayload);
      } else {
        await create(requestPayload);
      }

      navigate('/blogs');
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Failed to save blog.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/blogs')}>
          Back
        </Button>
        <Typography variant="h5">{isEdit ? 'Edit Blog' : 'New Blog'}</Typography>
      </Box>

      {loading && isEdit ? (
        <Typography>Loading blog...</Typography>
      ) : (
        <Paper sx={{ p: 3, display: 'grid', gap: 2 }}>
          <TextField
            label="Title"
            name="title"
            value={formState.title}
            onChange={handleChange}
            fullWidth
            required
          />
          <TextField
            label="Slug (optional)"
            name="slug"
            value={formState.slug}
            onChange={handleChange}
            fullWidth
            helperText="Leave empty to auto-generate from title."
          />
          <TextField
            label="Excerpt"
            name="excerpt"
            value={formState.excerpt}
            onChange={handleChange}
            fullWidth
            multiline
            minRows={2}
          />
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Content
            </Typography>
            <Paper variant="outlined" sx={{ p: 1 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                <IconButton
                  size="small"
                  color={editor?.isActive('bold') ? 'primary' : 'default'}
                  onClick={() => editor?.chain().focus().toggleBold().run()}
                >
                  <FormatBoldIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color={editor?.isActive('italic') ? 'primary' : 'default'}
                  onClick={() => editor?.chain().focus().toggleItalic().run()}
                >
                  <FormatItalicIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color={editor?.isActive('underline') ? 'primary' : 'default'}
                  onClick={() => editor?.chain().focus().toggleUnderline().run()}
                >
                  <FormatUnderlinedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color={editor?.isActive('strike') ? 'primary' : 'default'}
                  onClick={() => editor?.chain().focus().toggleStrike().run()}
                >
                  <StrikethroughSIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color={editor?.isActive('heading', { level: 2 }) ? 'primary' : 'default'}
                  onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
                >
                  <TitleIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color={editor?.isActive('bulletList') ? 'primary' : 'default'}
                  onClick={() => editor?.chain().focus().toggleBulletList().run()}
                >
                  <FormatListBulletedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color={editor?.isActive('orderedList') ? 'primary' : 'default'}
                  onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                >
                  <FormatListNumberedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color={editor?.isActive('blockquote') ? 'primary' : 'default'}
                  onClick={() => editor?.chain().focus().toggleBlockquote().run()}
                >
                  <FormatQuoteIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color={editor?.isActive('code') ? 'primary' : 'default'}
                  onClick={() => editor?.chain().focus().toggleCode().run()}
                >
                  <CodeIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color={editor?.isActive('codeBlock') ? 'primary' : 'default'}
                  onClick={() => editor?.chain().focus().toggleCodeBlock().run()}
                >
                  <CodeOffIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => editor?.chain().focus().setTextAlign('left').run()}>
                  <FormatAlignLeftIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => editor?.chain().focus().setTextAlign('center').run()}>
                  <FormatAlignCenterIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => editor?.chain().focus().setTextAlign('right').run()}>
                  <FormatAlignRightIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => {
                    const url = window.prompt('Enter link URL');
                    if (!url) return;
                    editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
                  }}
                >
                  <LinkIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => editor?.chain().focus().unsetLink().run()}>
                  <LinkOffIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => {
                    const url = window.prompt('Enter image URL');
                    if (!url) return;
                    editor?.chain().focus().setImage({ src: url }).run();
                  }}
                >
                  <ImageIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => editor?.chain().focus().undo().run()}>
                  <UndoIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => editor?.chain().focus().redo().run()}>
                  <RedoIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  minHeight: 220,
                  px: 2,
                  py: 1,
                  '& .ProseMirror': {
                    outline: 'none',
                    minHeight: 200
                  }
                }}
              >
                <EditorContent editor={editor} />
              </Box>
            </Paper>
          </Box>
          <TextField
            label="Tags (comma separated)"
            name="tagsText"
            value={formState.tagsText}
            onChange={handleChange}
            fullWidth
          />

          <Divider />

          <Typography variant="subtitle1">Cover Image</Typography>
          <Button variant="outlined" component="label">
            Upload Cover
            <input type="file" hidden accept="image/*" onChange={handleCoverChange} />
          </Button>
          {coverPreview ? (
            <Box sx={{ maxWidth: 520 }}>
              <img
                src={coverPreview}
                alt="cover preview"
                style={{ width: '100%', borderRadius: 8, objectFit: 'cover' }}
              />
            </Box>
          ) : null}

          <Divider />

          <Typography variant="subtitle1">SEO</Typography>
          <TextField
            label="Meta Title"
            name="metaTitle"
            value={formState.seo.metaTitle}
            onChange={handleSeoChange}
            fullWidth
          />
          <TextField
            label="Meta Description"
            name="metaDescription"
            value={formState.seo.metaDescription}
            onChange={handleSeoChange}
            fullWidth
            multiline
            minRows={2}
          />
          <TextField
            label="Canonical URL"
            name="canonicalUrl"
            value={formState.seo.canonicalUrl}
            onChange={handleSeoChange}
            fullWidth
          />

          <Divider />

          <FormControlLabel
            control={
              <Switch
                checked={formState.isPublished}
                onChange={(event) => setFormState((prev) => ({ ...prev, isPublished: event.target.checked }))}
              />
            }
            label="Published"
          />

          {errorMessage ? <Typography color="error">{errorMessage}</Typography> : null}

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Saving...' : isEdit ? 'Update Blog' : 'Create Blog'}
            </Button>
            <Button variant="outlined" onClick={() => navigate('/blogs')} disabled={submitting}>
              Cancel
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default BlogForm;
